import { create, muiDialogV5, useModal } from '@ebay/nice-modal-react'
import React, { useState } from 'react'
import { Box, Button as MuiButton, Typography, styled } from '@mui/material'
import { useNetwork } from 'wagmi'
import { commify } from 'ethers/lib/utils'
import dayjs, { unix } from 'dayjs'
import { toast } from 'react-toastify'
import { useBoolean } from 'ahooks'
import Check from '../Check'
import Countdown from './Countdown'
import Dialog from '@/components/common/Dialog'
import { FlexCol } from '@/components/common/Columns'
import { SupportedTicker } from '@/constants/token'
import { formatNumber, getTokenDecimals } from '@/utils'
import { useClaim, useUserEarned } from '@/hooks/farmHooks'
import { Row, RowBetweenX, RowCenterY } from '@/components/common/Rows'
import Helper from '@/components/common/Helper'

const Tag = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;

  padding: 2px 8px;

  background: rgba(191, 255, 11, 0.2);

  margin-right: 4px;

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  color: #bfff0b;
`

export const MinimumAmountWrapper = styled(Box)`
  display: flex;
  align-items: center;
  column-gap: 8px;

  margin-top: 10px;
  align-self: flex-end;
`
export const Text3 = styled(Typography)`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #ffffff;
`

const Button = styled(MuiButton)`
  height: 48px;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
`

const ClaimDialog = ({
  tickerName,
  rewardTickerName,
  poolId,
  onSuccess,
  price,
  endAt,
  lockTime,
}: {
  tickerName: SupportedTicker
  rewardTickerName: SupportedTicker
  poolId: number
  onSuccess: () => void
  endAt: number
  price?: number
  lockTime: number
}) => {
  const modal = useModal()

  const { chain } = useNetwork()

  const decimal = getTokenDecimals(tickerName, chain?.id)

  const [isChecked, { toggle: toggleChecked }] = useBoolean()

  const { data: userEarned, refresh: refreshUserEarned } = useUserEarned(poolId)

  const { run: claim, loading: isClaiming } = useClaim(poolId, {
    onSuccess: () => {
      toast.success('Claimed successfully')
      setTimeout(() => {
        onSuccess()
        refreshUserEarned()
      }, 500)
      modal.hide()
    },
  })

  const [isClaimable, setIsClaimable] = useState<boolean>(unix(endAt).isBefore(dayjs()))

  const earnedAmountText = userEarned && decimal ? formatNumber(userEarned.toString(), decimal) : '-'

  return (
    <Dialog {...muiDialogV5(modal)}>
      <FlexCol sx={{ rowGap: { xs: 24, md: 40 }, minWidth: { xs: 'auto', md: 450 } }}>
        <Typography fontSize={{ xs: 24, md: 32 }}>Claim</Typography>

        <FlexCol sx={{ rowGap: { xs: 12, md: 32 } }}>
          <RowBetweenX>
            <FlexCol>
              <RowCenterY sx={{ columnGap: 4 }}>
                <Typography
                  sx={{
                    color: '#99999A',
                    fontSize: '12px',
                  }}
                >
                  {isClaimable ? 'Claimable' : 'Lock'}
                </Typography>
                {!isClaimable && (
                  <Helper
                    toolTipProps={{
                      title:
                        'The staked income will be locked for one day by default. Each time the stake is increased, the locking time will be reset.',
                    }}
                    iconProps={{ sx: { width: 16, height: 16, color: '#FFFFFF80' } }}
                  />
                )}
              </RowCenterY>
              <Row sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>{earnedAmountText}</Typography>
                <Typography sx={{ fontSize: '12px', fontWeight: 400, opacity: 0.7, ml: 4 }}>
                  ${' '}
                  {userEarned && decimal && typeof price === 'number'
                    ? commify(
                        (
                          Number(
                            formatNumber(userEarned.toString(), decimal, {
                              shouldCommify: false,
                            }),
                          ) * price
                        ).toFixed(2),
                      )
                    : '-'}
                </Typography>
              </Row>
            </FlexCol>

            {isClaimable === false && (
              <Countdown
                unlockTimestamp={endAt}
                onEnd={() => {
                  setIsClaimable(true)
                }}
              />
            )}
          </RowBetweenX>

          <RowCenterY
            sx={{ py: 16, px: 20, border: '1px solid rgba(191, 255, 11, 0.20)', bgcolor: 'rgba(191, 255, 11, 0.10)' }}
          >
            <Typography sx={{ fontSize: '16px', fontWeight: 400, opacity: 0.7 }}>You will receive</Typography>
            <Typography sx={{ ml: 'auto', fontSize: '20px', fontWeight: 700 }}>
              {earnedAmountText} {rewardTickerName}
            </Typography>
            <Tag sx={{ ml: 8 }}>ERC 20</Tag>
          </RowCenterY>

          <Check value={isChecked} toggle={toggleChecked} lockTime={lockTime} action="claim" />

          <Button variant="contained" onClick={claim} disabled={isClaiming || !isClaimable || !isChecked}>
            {isClaiming ? 'Claiming...' : 'Claim'}
          </Button>
        </FlexCol>
      </FlexCol>
    </Dialog>
  )
}

export default create(ClaimDialog)
